import React, { useContext } from "react";

import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useBreakpoint from "../../shared/hooks/use-breakpoint/useBreakpoint";
import {
  ToolContent,
  ToolMobileSettings,
  ToolbarContent,
  ToolSettings,
  LocationPrompt,
  IconPack,
  Icon,
  FeatureToggle,
  ReferenceLinkContent
} from "../../shared/models/app-data-model";
import SetLocation from "../set-location/set-location";
import "./global-tool.scss";

export type GlobalToolProps = {
  content: ToolbarContent;
  iconPack: IconPack[];
  featureToggles: FeatureToggle[];
};

const GlobalTool: React.FC<GlobalToolProps> = ({ content, iconPack, featureToggles }: GlobalToolProps) => {
  const uiStateContext = useContext(UiStateContext);
  const breakpoint = useBreakpoint();
  const toolSettings: ToolSettings = content?.tool_settings;
  const toolContent: ToolContent = toolSettings?.tool_content;
  const mobile_settings: ToolMobileSettings = toolSettings?.mobile_settings;
  const onclick_behavior = toolContent?.onclick_behavior;
  const hasIcon = toolContent.tool_icon?.icon_source?.url.length > 0;
  const mobileIcon = mobile_settings?.content_display == "text_and_icon" || mobile_settings?.content_display == "icon_only";
  const mobileText = mobile_settings?.content_display == "text_and_icon" || mobile_settings?.content_display == "text_only";
  const displayIcon = (breakpoint > 0 && hasIcon) || (breakpoint == 0 && hasIcon && mobileIcon);
  const displayText = breakpoint > 0 || (breakpoint == 0 && mobileText);
  const isBrowser = typeof document !== "undefined";

  // Set Your Location tool
  const setLocationContent = onclick_behavior.find((item) => item.internal?.type == "sh_location_prompt");

  // Link to pages tool
  const linkContent = onclick_behavior.find(
    (item) =>
      item.internal?.type === "sh_pages" ||
      item.internal?.type === "sh_external_link" ||
      item.internal?.type === "sh_regional_link_component"
  );
  
  // TODO: Need to add supportedLanguages in the future

  const toolIcon = {
    svg: {
      content_type: "image/svg+xml",
      localAsset: toolContent.tool_icon.icon_source.localAsset,
      title: toolContent.tool_icon.alt_text,
      url: toolContent.tool_icon.icon_source.url
    }
  } as Icon;

  // Only render SetLocation at runtime so we can have time to read the saved value for the location input text
  const setLocation = isBrowser && setLocationContent && (
    <SetLocation content={setLocationContent as LocationPrompt} icon={toolContent.tool_icon} iconPack={iconPack} />
  );

  return (
    <>
      {setLocationContent && (
        <li className="tool-item" data-testid="set-location-tool-item" id="set-location-tool-item" tabIndex={0}>
          <div className="action-link">
            {displayIcon && <ShIcon className="action-link-icon" icon={toolIcon} />}
            {displayText && <span>{uiStateContext?.userLocationText ? uiStateContext?.userLocationText : toolContent?.text}</span>}
          </div>
          {setLocation}
        </li>
      )}
      {linkContent && (
        <li className="tool-item" data-testid="link-tool-item">
          <ShLink
            className="action-link"
            featureToggles={featureToggles}
            reference={toolContent.onclick_behavior as ReferenceLinkContent[]}
            iconPack={iconPack}
            regionalLinkAsButton={true}
            closeMenus={true}
          >
            {toolIcon && <ShIcon className="action-link-icon" icon={toolIcon} />}
            {toolContent.text && <span>{toolContent.text}</span>}
          </ShLink>
        </li>
      )}
    </>
  );
};

export default GlobalTool;
